// Configuration for the HappyBridge install wizard.
//
// Mirrors the shape of features/onboarding/setup-flow-config.ts so the two flows stay
// visually and structurally consistent.
//
// Design doc:  specs/2026-07-29-happy-bridge-install-wizard-design.md
// Wiring spec: specs/2026-07-29-happy-bridge-wizard-wiring-spec.md

import type { BridgeIntegrationId } from "@common/lib/api/bridge";

// --- Step IDs ---

export type BridgeStepId =
    | "computer"
    | "timezone"
    | "install"
    | "suite"
    // EN-997. Cornerstone/ImproMed only, between suite and staff_names: maps Bitwerx-era doctor rows
    // onto the bridge's provider rows before the cutover. Advances on its own when nothing needs it.
    | "doctor_mapping"
    | "calendar"
    | "staff_names"
    | "sync"
    | "done";

/**
 * The markers worth resuming onto. "computer" is the same as no progress, "timezone" asks a
 * question that is re-asked unconditionally anyway, and "done" means they finished.
 *
 * Exported as one list because two places need the same answer and had their own copies: the
 * entry-time `hasResumableStep`, and `persistStep`, which must not overwrite one of these with a
 * step outside the set (see the demotion guard there).
 */
export const RESUMABLE_STEPS = [
    "install",
    "suite",
    "doctor_mapping",
    "calendar",
    "staff_names",
    "sync",
] as const satisfies readonly BridgeStepId[];

export const isResumableStep = (step: BridgeStepId | null | undefined): boolean =>
    step != null && (RESUMABLE_STEPS as readonly string[]).includes(step);

/** How the wizard groups appointments into calendar columns. Persists to account.calendar_mode. */
export type ColumnMode = "doctors" | "resources";

/**
 * How the initial staff/room import ended.
 *
 * `partial` is its own outcome, not a flavour of `ok`: POST /bridge/sync-providers returns 200
 * with zeroed resource counts when the resources fetch fails, so reporting counts without
 * checking would tell a clinic "Found 12 staff and 0 rooms." as a completed import and then hand
 * them an empty room panel for a reason we already knew.
 *
 * Carried into the calendar step as well, because an empty schedule there means something
 * different depending on this: after a failed import it's our import, not the clinic's PIMS. The
 * calendar step is on AVImark's main path and reachable by the others, so this is not a
 * Cornerstone-only distinction.
 */
export type ImportState = "idle" | "running" | "ok" | "partial" | "failed";

/**
 * The connect screen's user-facing state. Deliberately NOT the bridge's internal health
 * vocabulary — no "degraded", no db/pubsub flags. Each state answers only: did it work,
 * will it keep working, what do I do.
 */
export type ConnectState =
    | "waiting"
    | "stalled"
    | "settingUp"
    // `settingUp` with the same threshold's worth of time behind it. "Connected — finishing
    // setup" spins indefinitely otherwise: nothing bounds how long a bridge stays degraded, and
    // this screen is the one a clinic sits on after their old connector has already been
    // switched off. Offers help rather than a verdict — a degraded bridge may still be
    // recovering, and we can see more from our side than this screen can.
    | "settingUpStalled"
    | "working"
    | "notWorking"
    // Our own status endpoint isn't answering, so we have no idea what the bridge is doing.
    // Deliberately distinct from both `waiting` and `stalled`: those two blame the clinic's
    // install ("did the installer finish? did it run as administrator?") and a clinic that
    // follows that advice during OUR outage may uninstall a working bridge.
    | "cantCheck"
    // EN-990. The bridge reported that it is not on the Cornerstone database
    // server. A terminal state, deliberately with no way forward: the calendar
    // and sync steps would configure a connection that cannot work. Rare by
    // design, because the installer now refuses this machine - it exists for a
    // hand-copied install or one done with a pre-EN-990 installer.
    | "wrongMachine";

/**
 * Why we can't confirm things are working. Three of the observed failure modes are
 * indistinguishable from the cloud (installer never ran / no admin rights / port 443
 * blocked all produce silence), so those collapse into the "stalled" state rather than
 * a guessed diagnosis.
 */
export type NotWorkingKind =
    | "lostContact" // was connected, went quiet
    | "cantReachPims" // reaches us, can't reach the clinic's PIMS (whichever one it is)
    | "unknown"; // reaches both, round-trip still fails — Bobby's case

/**
 * Whether this PIMS gets the suite step instead of the room-vs-staff question.
 *
 * True for Cornerstone AND ImproMed — the two SQL PIMS — so BOTH take the suite step and neither
 * is asked the room-vs-staff question. The reasoning below is written in Cornerstone's terms
 * because that is where it was established, and it was left reading as a Cornerstone-vs-AVImark
 * binary long after `sqlserver` joined the condition: a maintainer reading only this paragraph
 * would not learn that ImproMed is affected at all. It is (see `bridgeBackMap`, which gets this
 * right: "`sync`'s predecessor is `suite` on Cornerstone/ImproMed").
 *
 * Cornerstone calendar columns are ALWAYS rooms. That is structural, not a setting: IDEXX's own
 * help says the room name "is used as the column header", and the two staff-flavoured controls
 * on that window filter and annotate rather than re-column. Practices that want a doctor view
 * create rooms named after doctors, which is IDEXX's documented pattern and is why
 * `Dr Lauren Colby` and `Walk In/ UC` sit side by side as peers. So there is nothing to ask.
 *
 * AVImark is the one PIMS that can genuinely change its calendar layout, so it keeps the
 * question. An unknown module type keeps it too: defaulting to a silent choice is worse than
 * one extra screen, because the wrong `calendar_mode` is invisible to the clinic afterwards.
 */
export const usesSuiteStep = (moduleType: string | undefined): boolean =>
    moduleType === "sybase" || moduleType === "sqlserver";

// --- Scout speech per step ---

export const BRIDGE_SCOUT_SPEECH: Record<BridgeStepId, { bold: string; sub: string }> = {
    computer: { bold: "Let's connect Cornerstone.", sub: "First, the right computer." },
    timezone: { bold: "Quick check.", sub: "What time zone is this clinic on?" },
    install: { bold: "Time to install.", sub: "I'll watch for it to connect." },
    suite: { bold: "Connected!", sub: "Which rooms should I show?" },
    doctor_mapping: { bold: "One more check.", sub: "Which provider is each doctor?" },
    calendar: { bold: "Connected!", sub: "Now let's match your schedule." },
    staff_names: { bold: "Looking good.", sub: "How should staff names read?" },
    sync: { bold: "Almost done.", sub: "Who should I keep track of?" },
    done: { bold: "All set!", sub: "I'll take it from here." },
};

/**
 * The scout's line for a given step, with the PIMS swapped in on the one line that names it.
 *
 * `computer` is the only entry above that names a PIMS ("Let's connect Cornerstone."); the rest
 * ("Quick check.", "Time to install.") read the same regardless of which PIMS the clinic runs.
 * So this stays a small derivation over the flat table above rather than widening
 * `BRIDGE_SCOUT_SPEECH` itself into `Record<BridgeIntegrationId, Record<BridgeStepId, ...>>` —
 * that would triplicate six lines that never vary by PIMS just to let one or two vary.
 *
 * `integration` is `null` while `RightComputerStep`'s picker is still up (neither `module_type`
 * nor `accounts.current_pims` could say) — there is no PIMS to name yet, so this falls back to a
 * PIMS-agnostic line rather than guessing Cornerstone or printing "undefined".
 *
 * `done` is the table's other special case, but for a different reason than `computer`: it isn't
 * about naming, it's about claiming. The table's line — "All set! / I'll take it from here." — is
 * true for Cornerstone the moment this step is reached (appointments flow, documents export), but
 * not for AVImark: no anchor template has been discovered for the practice yet (an export attempt
 * 400s) and no export field mappings exist for its document types (an export that does run writes
 * a structurally complete but clinically empty exam). `DoneStep`'s right-panel content already
 * says so; the left-panel scout can't say the opposite next to it.
 */
export function getScoutSpeech(
    step: BridgeStepId,
    integration: BridgeIntegrationId | null,
): { bold: string; sub: string } {
    if (step === "computer") {
        return {
            bold: integration ? `Let's connect ${PIMS_COPY[integration].name}.` : "Let's get connected.",
            sub: BRIDGE_SCOUT_SPEECH.computer.sub,
        };
    }
    if (step === "done" && integration === "avimark") {
        return { bold: "Almost there.", sub: "I've got one more thing to finish on my end." };
    }
    return BRIDGE_SCOUT_SPEECH[step];
}

// --- What the clinic calls each column mode ---

/**
 * "Staff" rather than "doctors": Cornerstone's own list is staff records, which includes techs
 * and groomers, and the calendar step offers the choice as Room vs Staff. Three screens name
 * the mode, so they read it from here rather than each keeping its own ternary.
 */
export const COLUMN_MODE_NOUN: Record<ColumnMode, { singular: string; plural: string; title: string }> = {
    // "staff" is already both — a clinic says "one staff member", never "one staff", so the
    // singular carries the word the count reads naturally with.
    resources: { singular: "room", plural: "rooms", title: "Rooms" },
    doctors: { singular: "staff member", plural: "staff", title: "Staff" },
};

// --- What's left after the install ---

/**
 * Each configuration step named as the thing it asks the clinic for. Used both for the
 * button that leaves the install screen and for the list of what's still ahead on it.
 */
export const BRIDGE_STEP_ACTION_LABEL: Record<
    Extract<BridgeStepId, "suite" | "calendar" | "doctor_mapping" | "staff_names" | "sync">,
    string
> = {
    suite: "Choose your rooms",
    calendar: "Match your schedule",
    doctor_mapping: "Match your doctors",
    staff_names: "Choose how names appear",
    sync: "Choose who to sync",
};

/**
 * The steps still ahead once the bridge connects.
 *
 * The install screen shows these because a confirmed install reads as an ending otherwise —
 * it's the moment the thing the clinic downloaded starts working, but not the moment
 * appointments start arriving. The left rail's dots can't carry this: they're at 50% opacity
 * in a corner, and the whole rail is hidden below `md`, which is most of a tablet.
 *
 * Suite accounts don't get the sync step listed. It is conditional on the apply failing (EN-974),
 * and this list describes the path the clinic should expect — the success case. Naming a screen
 * most of them will never see would make the expected flow look longer than it is, and the
 * soft-fail detour explains itself on arrival. `staff_names` (EN-1242) is unconditional on both
 * arms, so both list it.
 */
export const remainingStepsAfterInstall = (moduleType: string | undefined): string[] =>
    usesSuiteStep(moduleType)
        ? [BRIDGE_STEP_ACTION_LABEL.suite, BRIDGE_STEP_ACTION_LABEL.staff_names]
        : [BRIDGE_STEP_ACTION_LABEL.calendar, BRIDGE_STEP_ACTION_LABEL.staff_names, BRIDGE_STEP_ACTION_LABEL.sync];

// --- Integration row predicates ---

/**
 * Whether an `account_integrations` set has the given provider ACTIVE — i.e. whether the exclusive
 * flip onto it has landed.
 *
 * The same predicate `IntegrationProvider` uses to derive `defaultIntegration`
 * (`IntegrationProvider.tsx:110-118`), specialised to one provider. Lives here and takes the provider value as an
 * argument because importing the `IntegrationProvider` enum would pull the settings module (and with it a
 * real Supabase client) into every consumer of this config.
 *
 * Two callers must agree on this answer and must not drift: the wizard's own read of
 * `pmsIntegrationData`, and the post-failure re-check that decides whether it is honest to tell a
 * clinic nothing changed.
 */
export const isProviderActive = (
    rows: { is_active?: boolean | null; integrations?: { integration_name?: string | null } | null }[] | null,
    providerValue: string,
): boolean => !!rows?.some((row) => row.is_active === true && row.integrations?.integration_name === providerValue);

/**
 * Whether an `account_integrations` set has a row for the given provider AT ALL, active or not.
 *
 * EN-1302: `setupBridgeIntegration` no longer always activates the row it creates. An account
 * with another live connector gets an inactive happybridge row that the server flips later, once
 * the bridge passes its cutover gate. The post-failure re-check uses both: `isProviderActive` for
 * "did the switch land?", and this one to tell a deferred switch (row present, inactive, nothing
 * switched) apart from no row at all. `isProviderActive` stays strict for `hasFlipped`, which
 * really does mean the switch happened.
 */
export const isProviderPresent = (
    rows: { integrations?: { integration_name?: string | null } | null }[] | null,
    providerValue: string,
): boolean => !!rows?.some((row) => row.integrations?.integration_name === providerValue);

// --- Progress dots ---
//
// Dots count screens the user sees, not internal states. "install" is one dot even though
// it moves through several connect states, and "done" isn't a dot at all.

export const BRIDGE_STEP_PROGRESS: Record<BridgeStepId, number> = {
    computer: 0,
    timezone: 1,
    install: 2,
    // `suite` shares `calendar`'s slot rather than getting its own: the two are mutually
    // exclusive per account (see `usesSuiteStep`), so exactly one of them is ever shown, and
    // giving it a separate number would either skip a dot for suite accounts or advance the
    // count past five for everyone else.
    suite: 3,
    calendar: 3,
    // EN-997. Suite arm only, so calendar accounts go from 3 straight to 5. The dots fill
    // cumulatively (`i <= progress`), so that reads as two filling at once, not one left empty.
    doctor_mapping: 4,
    staff_names: 5,
    sync: 6,
    done: 6,
};

/**
 * Seven dots: the six screens that ask something, plus the install. "done" isn't a dot.
 *
 * The time zone step is unconditional (it re-confirms even a value we already have, because a
 * wrong stored zone is exactly as damaging as a missing one and is otherwise invisible), and so is
 * the staff-name step (EN-1242). A dot that never advances for some accounts reads as broken.
 *
 * The suite/calendar split above doesn't add one — they occupy one slot between them, not two.
 * Suite accounts whose apply succeeds skip sync and land on `done`, which shares sync's slot, so
 * the last dot still fills for them.
 *
 * `doctor_mapping` (EN-997) does add one, though only the suite arm shows it; see its entry above.
 */
export const BRIDGE_DOT_COUNT = 7;

// --- Back navigation ---
//
// Deliberately no route back into "install": once the bridge has connected, re-showing
// the install screen would invite uninstalling a working connection. Configuration steps
// are freely revisitable.

/**
 * Back routing, which depends on which of the two mutually exclusive steps was shown.
 * A static map can't express that — `staff_names`'s predecessor is `suite` on Cornerstone/ImproMed
 * and `calendar` everywhere else — so this takes `moduleType` and defers to `usesSuiteStep` for the
 * same answer the step-selection logic gives, rather than keeping a second copy of that rule.
 * (Before EN-1242 that was `sync`'s predecessor; `sync` now always follows `staff_names`.)
 *
 * Still deliberately no route back into "install": once the bridge has connected, re-showing
 * the install screen would invite uninstalling a working connection.
 */
export const bridgeBackMap = (moduleType: string | undefined): Partial<Record<BridgeStepId, BridgeStepId>> => ({
    // Nothing has been downloaded yet at this point, so backing out to re-answer the
    // which-computer question is free.
    timezone: "computer",
    // EN-997. On the suite arm `doctor_mapping` sits between suite and staff_names.
    doctor_mapping: "suite",
    staff_names: usesSuiteStep(moduleType) ? "doctor_mapping" : "calendar",
    sync: "staff_names",
});

/**
 * EN-997. Runs `advance` only while the wizard is still on (or headed for) `expected`, and says
 * whether it did. For completions that can land after the clinic has moved on — a doctor-mapping
 * save that resolves after Back must not pull them forward again.
 */
export const advanceIfStillOn = (current: BridgeStepId, expected: BridgeStepId, advance: () => void): boolean => {
    if (current !== expected) return false;
    advance();
    return true;
};

// --- Activity window ---

/**
 * Days of appointment history used to rank columns on the calendar step and to decide
 * what's pre-checked on the sync step. Shorter and a vet returning from leave arrives
 * unchecked; longer and retired doctors creep back in.
 */
export const ACTIVITY_WINDOW_DAYS = 60;

/** How long the install screen waits in silence before offering the three checks. */
export const STALL_AFTER_MS = 10 * 60 * 1000;

/**
 * How long the Done screen will wait for the first appointment pull before letting the clinic
 * leave anyway.
 *
 * The pull is a courtesy, not a gate: it saves the clinic finding the Sync button on a calendar
 * they were just promised would fill up. A wedged or very slow PIMS must not strand them on the
 * last screen of a cutover, and the request continues server-side regardless of what this screen
 * does next.
 */
export const APPOINTMENT_SYNC_MAX_WAIT_MS = 20_000;

/**
 * How many `sync_excluded` writes the sync step sends at once.
 *
 * There is no bulk endpoint — API-Server exposes only `PATCH /doctors/:id` and
 * `PATCH /resources/:id` — so unchecking a list writes one request per row. A decades-old
 * Cornerstone can carry a hundred-odd staff records with a handful still in use, and that whole
 * list arriving unchecked is the ordinary case, not the pathological one.
 *
 * 10 because browsers cap concurrent connections per origin around 6 on HTTP/1.1 anyway: past
 * that the requests queue in the browser instead of the page, so a larger window buys nothing
 * and a smaller one just adds round trips.
 */
export const SYNC_WRITE_CHUNK_SIZE = 10;

/**
 * Most rows the mini calendar will draw, per panel.
 *
 * `deriveTimeSlots` derives one row per distinct appointment time, so a clinic booking on the
 * quarter-hour across a twelve-hour day produces ~48 rows — rendered twice, side by side, on a
 * screen whose only job is "does this look like my Tuesday?". Recognition happens in the first
 * few rows; the rest is scrolling. 12 covers a normal morning at half-hour spacing.
 */
export const MAX_TIME_SLOTS = 12;

/**
 * How long something has been quiet, in words, for user-facing copy.
 *
 * Raw minutes stop being information fast: a stale installation from three days ago rendered
 * "It stopped answering about 4320 minutes ago", which reads as a bug in the page rather than a
 * fact about the bridge. Minutes below the hour, then hours, then days.
 *
 * Returns null for 0 or unknown — "about 0 minutes ago" is worse than saying nothing, and the
 * caller has a sentence to drop rather than a number to print.
 */
export const describeElapsed = (minutes: number): string | null => {
    if (!Number.isFinite(minutes) || minutes < 1) return null;
    if (minutes < 90) return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
    const hours = Math.round(minutes / 60);
    if (hours < 36) return `${hours} ${hours === 1 ? "hour" : "hours"}`;
    const days = Math.round(hours / 24);
    return `${days} ${days === 1 ? "day" : "days"}`;
};

/**
 * What each PIMS is called on screen, and where its bridge has to live.
 *
 * One table rather than a ternary per component, matching `COLUMN_MODE_NOUN` — three screens name
 * the PIMS and they must not drift. The machine requirement differs in KIND, not just in noun:
 * Cornerstone's is enforced server-side (EN-990 refuses a workstation install), while AVImark's
 * follows from EN-979 detecting the RemObjects port on localhost, so the bridge must sit on the
 * machine running AVImarkServer.exe. That difference is why the strictness is worded per PIMS and
 * never centralised into one sentence: "will only install" is a promise about the installer, and
 * it is true for Cornerstone and ImproMed and NOT true for AVImark.
 *
 * Two forms of the same fact, because two screens frame it differently:
 *
 * - `machineRequirement` is the standalone sentence, naming the machine inside itself. The wizard's
 *   wrong-machine screen (`RightComputerStep`) renders it under its own heading.
 * - `machineRule` is the same claim with the machine factored out into `serverNoun`, for a slot
 *   that has already named the machine in a bold lead and would otherwise stutter — the upgrade
 *   modal's requirements list, via `bridgeMachineRequirement` in `bridgeUpgradeCopy.ts`.
 *
 * They must make the SAME promise; `bridge-setup-config.spec.ts` pins the modal verb across both
 * so a reworded `machineRequirement` cannot leave `machineRule` promising something stricter.
 */
export const PIMS_COPY: Record<
    BridgeIntegrationId,
    { name: string; serverNoun: string; machineRequirement: string; machineRule: string }
> = {
    cornerstone: {
        name: "Cornerstone",
        serverNoun: "Cornerstone Server",
        machineRequirement: "HappyBridge will only install on the Windows computer that runs the Cornerstone Server.",
        machineRule: "HappyBridge will only install on the Windows computer that runs it, not on a front desk machine.",
    },
    avimark: {
        name: "AVImark",
        serverNoun: "AVImark server",
        machineRequirement:
            "HappyBridge has to run on the Windows computer that runs AVImark's server, so it can find AVImark on startup.",
        machineRule:
            "HappyBridge has to run on the Windows computer that runs it, not on a front desk machine, so it can find AVImark on startup.",
    },
    impromed: {
        name: "ImproMed",
        serverNoun: "ImproMed server",
        machineRequirement: "HappyBridge will only install on the Windows computer that runs the ImproMed database.",
        machineRule: "HappyBridge will only install on the Windows computer that runs it, not on a front desk machine.",
    },
};

/**
 * The bridge module type each integration registers as — the inverse of API-Server's
 * `INTEGRATION_TO_MODULE_TYPE`.
 *
 * A `Record` over `BridgeIntegrationId`, not a switch, and that is the whole point: TypeScript
 * checks a Record's keys for COMPLETENESS, but only checks a switch's or a ternary's return value
 * for ASSIGNABILITY. The previous shape of this mapping was a switch, so adding a fourth
 * `BridgeIntegrationId` compiled clean and silently returned `null` for the new PIMS forever —
 * while `PIMS_COPY`, a Record, failed the build the way it should. Same fact, two enforcement
 * levels, and the weaker one guarded the value that decides which installer a clinic is handed.
 * Everything downstream of here now derives from these two Records rather than restating the ids.
 */
export const MODULE_TYPE_BY_INTEGRATION: Record<BridgeIntegrationId, string> = {
    cornerstone: "sybase",
    impromed: "sqlserver",
    avimark: "avimark",
};

/**
 * Whether API-Server can write a document BACK into this PIMS through the bridge.
 *
 * Not the same question as "is there a bridge module", which is what `PIMS_COPY` and
 * `MODULE_TYPE_BY_INTEGRATION` answer, and the difference is load-bearing. The bridge has an
 * ImproMed module and READS through it perfectly well — but `integrationController.ts:634-636`
 * dispatches HappyBridge writeback for `avimark` and `sybase` only and answers everything else
 * with "HappyBridge writeback not supported for module_type '<type>'". ImproMed's module type is
 * `sqlserver`, and no `exportDocumentToImpromed` exists anywhere in API-Server.
 *
 * That matters because `setup-integration` runs `activate_integration_exclusive`: moving a
 * practice onto the bridge deactivates whatever they were using before. An ImproMed clinic writes
 * back today through the third-party service, so completing this wizard would take a working
 * export path away and replace it with one that returns an error for every document.
 *
 * A `Record`, not an array or a set, for the reason `MODULE_TYPE_BY_INTEGRATION` above is one:
 * TypeScript checks a Record's keys for COMPLETENESS. A fourth `BridgeIntegrationId` fails the
 * build here and has to be answered deliberately, rather than defaulting to "writeback works"
 * and quietly being offered to clinics.
 *
 * Delete ImproMed's `false` when `exportDocumentToImpromed` ships — the specs name it.
 */
export const BRIDGE_WRITEBACK_IMPLEMENTED: Record<BridgeIntegrationId, boolean> = {
    cornerstone: true,
    avimark: true,
    impromed: false,
};

/** Whether we can move a practice onto the bridge without taking their writeback away. */
export const hasBridgeWriteback = (integration: BridgeIntegrationId): boolean =>
    BRIDGE_WRITEBACK_IMPLEMENTED[integration];

/**
 * Every integration a bridge module exists for, derived from `PIMS_COPY`'s keys so it cannot fall
 * out of step with it. Adding a PIMS to `BridgeIntegrationId` breaks the build in `PIMS_COPY` and
 * `MODULE_TYPE_BY_INTEGRATION`, and this list — plus the picker that renders from it — then follows
 * automatically.
 */
export const BRIDGE_INTEGRATION_IDS: readonly BridgeIntegrationId[] = Object.freeze(
    Object.keys(PIMS_COPY) as BridgeIntegrationId[],
);

/**
 * The subset of the above we can actually put a clinic on today. This is what the wizard's manual
 * picker offers to anyone outside the hand-managed beta: the picker only renders when we could not
 * read the account's PIMS at all, so without this an ImproMed practice whose `current_pims` is
 * blank could still select ImproMed and be walked into losing writeback.
 */
export const INSTALLABLE_INTEGRATION_IDS: readonly BridgeIntegrationId[] = Object.freeze(
    BRIDGE_INTEGRATION_IDS.filter(hasBridgeWriteback),
);

/**
 * `accounts.current_pims` is nullable and can still hold a clinic's PREVIOUS system during a switch,
 * so anything outside this set is not an answer — it is the signal to ask.
 *
 * Note what this canNOT catch, because it bit in review: a stale value that is ITSELF bridge-capable
 * passes straight through and is believed. `BridgeSetupFlow`'s `canCorrectPims` is the answer to
 * that; this function only ever screens out values with no bridge module at all.
 *
 * Type-only import: `bridge.ts` imports `ACTIVITY_WINDOW_DAYS` and `BridgeStepId` from this file, so
 * a value import would close a cycle.
 */
export const bridgeIntegrationForPims = (currentPims: string | null | undefined): BridgeIntegrationId | null =>
    BRIDGE_INTEGRATION_IDS.find((id) => id === currentPims) ?? null;

/**
 * Authoritative once the bridge has registered, because it reflects what is actually installed
 * rather than what the account row says. Derived from `MODULE_TYPE_BY_INTEGRATION` so the two
 * directions cannot disagree.
 */
export const integrationForModuleType = (moduleType: string | undefined): BridgeIntegrationId | null =>
    moduleType === undefined
        ? null
        : (BRIDGE_INTEGRATION_IDS.find((id) => MODULE_TYPE_BY_INTEGRATION[id] === moduleType) ?? null);
